import React from 'react';
import {motion} from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import CTAButton from '../components/CTAButton';
import TrustSignals from '../components/TrustSignals';
import * as FiIcons from 'react-icons/fi';

const {FiTruck, FiShield, FiCheck, FiUsers, FiDollarSign, FiTool} = FiIcons;

const CommercialAutoPage = () => {
  const coverageAreas = [
    {
      title: "Underinsured motorists",
      description: "When another driver's coverage won't cover an incident with your vehicle.",
      icon: FiShield
    },
    {
      title: "Rental reimbursement", 
      description: "Get up to $100 per day if your business vehicle is in the shop.",
      icon: FiDollarSign
    },
    {
      title: "Towing and labor",
      description: "If you need a tow, you could get help with the costs.",
      icon: FiTool
    },
    {
      title: "Collision & comprehensive coverage",
      description: "Get help if you need repairs or replacements — fast.",
      icon: FiTruck
    },
    {
      title: "Injuries from auto accidents",
      description: "Assistance with legal fees, medical bills and more.",
      icon: FiUsers
    },
    {
      title: "Damage to other people's property",
      description: "Clipped a mailbox with your side mirror? Coverage may help.",
      icon: FiShield
    }
  ];

  const businessTypes = [
    "Deliver goods, such as pizza, flowers or other products",
    "Provide a service that regularly uses a vehicle, such as mobile pet grooming or a food truck", 
    "Transport equipment you use for work, such as cleaning equipment or carpentry tools",
    "Travel to job sites for work, like in construction, landscaping, real estate and more",
    "Tow a trailer carrying your work equipment",
    "Drive your employees to job sites"
  ];

  const costFactors = [
    "Your insurance history",
    "The number of vehicles you have", 
    "The types of vehicles you have",
    "How you use your business transportation",
    "The limits you choose",
    "The state where you work"
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary-600 to-primary-800 text-white py-20">
        <div className="absolute inset-0 bg-black opacity-60"></div>
        <div className="absolute inset-0 bg-cover bg-center opacity-40"
          style={{backgroundImage: 'url(https://images.unsplash.com/photo-1449824913935-59a10b8d2000?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80)'}}
        ></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <motion.h1
              initial={{opacity: 0, y: 30}}
              animate={{opacity: 1, y: 0}}
              transition={{duration: 0.8}}
              className="text-4xl md:text-6xl font-bold mb-6"
            >
              Commercial Auto Insurance: Protect Your Business Vehicles
            </motion.h1>
            <motion.p
              initial={{opacity: 0, y: 30}}
              animate={{opacity: 1, y: 0}}
              transition={{duration: 0.8, delay: 0.2}}
              className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto"
            >
              Essential coverage for delivery vehicles, work trucks, and business transportation. Get protected today!
            </motion.p>
            <motion.div
              initial={{opacity: 0, y: 30}}
              animate={{opacity: 1, y: 0}}
              transition={{duration: 0.8, delay: 0.4}}
            >
              <CTAButton text="Get Your Commercial Auto Quote" size="xl" className="mb-8" />
            </motion.div>
            <motion.div
              initial={{opacity: 0}}
              animate={{opacity: 1}}
              transition={{duration: 0.8, delay: 0.6}}
            >
              <TrustSignals className="text-white" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Coverage Areas */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Small Business Commercial Auto Insurance Could Help Protect You From:
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {coverageAreas.map((area, index) => (
              <motion.div
                key={area.title}
                initial={{opacity: 0, y: 30}}
                animate={{opacity: 1, y: 0}}
                transition={{duration: 0.6, delay: index * 0.1}}
                className="text-center p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow"
              >
                <div className="bg-primary-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                  <SafeIcon icon={area.icon} className="h-8 w-8 text-primary-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-4">{area.title}</h3>
                <p className="text-gray-600">{area.description}</p>
              </motion.div>
            ))}
          </div>
          <motion.div
            initial={{opacity: 0, y: 30}}
            animate={{opacity: 1, y: 0}}
            transition={{duration: 0.6, delay: 0.8}}
            className="text-center mt-12"
          >
            <CTAButton text="Get Instant Quote" />
          </motion.div>
        </div>
      </section>

      {/* What is Commercial Auto Insurance */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{opacity: 0, x: -30}}
              animate={{opacity: 1, x: 0}}
              transition={{duration: 0.8}}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                What is Commercial Auto Insurance?
              </h2>
              <p className="text-lg text-gray-700 mb-6">
                If you use a vehicle for work, you may need a commercial auto liability insurance policy if you:
              </p>
              <ul className="space-y-3">
                {businessTypes.map((type, index) => (
                  <motion.li
                    key={index}
                    initial={{opacity: 0, x: -20}}
                    animate={{opacity: 1, x: 0}}
                    transition={{duration: 0.6, delay: index * 0.1}}
                    className="flex items-start space-x-3"
                  >
                    <SafeIcon icon={FiCheck} className="h-5 w-5 text-secondary-600 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">{type}</span>
                  </motion.li>
                ))}
              </ul>
            </motion.div>
            <motion.div
              initial={{opacity: 0, x: 30}}
              animate={{opacity: 1, x: 0}}
              transition={{duration: 0.8}}
              className="bg-primary-50 p-8 rounded-lg"
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Key Benefits</h3>
              <div className="space-y-4 text-gray-700">
                <p>• Protects your business from unexpected vehicle repair and replacement costs</p>
                <p>• Covers employees operating your work vehicles</p>
                <p>• Required by law in almost every state for business vehicles</p>
                <p>• Provides liability coverage for injuries and property damage</p>
                <p>• Often required by clients for certificates of insurance</p>
              </div>
              <div className="mt-6 p-4 bg-yellow-50 border-l-4 border-yellow-400 rounded">
                <p className="text-sm text-yellow-800">
                  <strong>Note:</strong> NEXT does not directly provide commercial auto insurance, but we partner with insurers who can offer this coverage.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Legal Requirements */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Do I Need Commercial Auto Insurance? Is it Required by Law?
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <motion.div
              initial={{opacity: 0, y: 30}}
              animate={{opacity: 1, y: 0}}
              transition={{duration: 0.6}}
              className="bg-red-50 p-8 rounded-lg border-l-4 border-red-400"
            >
              <h3 className="text-xl font-bold text-gray-900 mb-4">Required by Law</h3>
              <p className="text-gray-700 mb-4">
                <strong>Yes</strong> — insurance for commercial vehicles is required in almost every state if you have vehicles you drive for business.
              </p>
              <p className="text-gray-700">
                Most states require liability insurance for injuries and property damage. Some also require uninsured/underinsured motorists coverage and medical payments coverage.
              </p>
            </motion.div>
            <motion.div
              initial={{opacity: 0, y: 30}}
              animate={{opacity: 1, y: 0}}
              transition={{duration: 0.6, delay: 0.2}}
              className="bg-blue-50 p-8 rounded-lg border-l-4 border-blue-400"
            >
              <h3 className="text-xl font-bold text-gray-900 mb-4">Personal Auto Won't Cover You</h3>
              <p className="text-gray-700 mb-4">
                Even if you already have a personal auto insurance policy, it probably won't cover accidents that happen while you're driving for business.
              </p>
              <p className="text-gray-700">
                Some clients might require you to have a certificate of insurance (COI) before they'll agree to work with you.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Cost Factors */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              What's the Cost of Commercial Auto Insurance?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Commercial auto insurance cost can vary depending on several factors:
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {costFactors.map((factor, index) => (
              <motion.div
                key={factor}
                initial={{opacity: 0, y: 30}}
                animate={{opacity: 1, y: 0}}
                transition={{duration: 0.6, delay: index * 0.1}}
                className="bg-white p-6 rounded-lg shadow-lg"
              >
                <div className="flex items-center space-x-3">
                  <SafeIcon icon={FiDollarSign} className="h-6 w-6 text-primary-600 flex-shrink-0" />
                  <span className="text-gray-700 font-medium">{factor}</span>
                </div>
              </motion.div>
            ))}
          </div>
          <motion.div
            initial={{opacity: 0, y: 30}}
            animate={{opacity: 1, y: 0}}
            transition={{duration: 0.6, delay: 0.8}}
            className="mt-12 text-center bg-primary-50 p-8 rounded-lg"
          >
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Get Your Exact Price</h3>
            <p className="text-lg text-gray-700 mb-6">
              The best way to learn exactly what you'll pay is to get a free commercial auto insurance quote online. It's no obligation, and it takes about 10 minutes to see your exact price.
            </p>
            <CTAButton text="Start Free Quote Today" />
          </motion.div>
        </div>
      </section>

      {/* Coverage Details */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              What Does Commercial Auto Insurance Cover?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Whether you have a single work truck or a small fleet of delivery vehicles, your commercial auto insurance policy could provide coverage up to your policy limit for:
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <motion.div
              initial={{opacity: 0, x: -30}}
              animate={{opacity: 1, x: 0}}
              transition={{duration: 0.8}}
              className="space-y-4"
            >
              <div className="flex items-start space-x-3">
                <SafeIcon icon={FiCheck} className="h-6 w-6 text-secondary-600 mt-1 flex-shrink-0" />
                <span className="text-gray-700">Expenses for claims related to emergency care</span>
              </div>
              <div className="flex items-start space-x-3">
                <SafeIcon icon={FiCheck} className="h-6 w-6 text-secondary-600 mt-1 flex-shrink-0" />
                <span className="text-gray-700">Medical bills after a vehicle accident</span>
              </div>
              <div className="flex items-start space-x-3">
                <SafeIcon icon={FiCheck} className="h-6 w-6 text-secondary-600 mt-1 flex-shrink-0" />
                <span className="text-gray-700">Damage to other people's property with your vehicle</span>
              </div>
              <div className="flex items-start space-x-3">
                <SafeIcon icon={FiCheck} className="h-6 w-6 text-secondary-600 mt-1 flex-shrink-0" />
                <span className="text-gray-700">Damage to your vehicle</span>
              </div>
            </motion.div>
            <motion.div
              initial={{opacity: 0, x: 30}}
              animate={{opacity: 1, x: 0}}
              transition={{duration: 0.8}}
              className="space-y-4"
            >
              <div className="flex items-start space-x-3">
                <SafeIcon icon={FiCheck} className="h-6 w-6 text-secondary-600 mt-1 flex-shrink-0" />
                <span className="text-gray-700">Legal fees around an accident or incident when driving for work</span>
              </div>
              <div className="flex items-start space-x-3">
                <SafeIcon icon={FiCheck} className="h-6 w-6 text-secondary-600 mt-1 flex-shrink-0" />
                <span className="text-gray-700">Gap coverage on financed and leased work vehicles</span>
              </div>
              <div className="flex items-start space-x-3">
                <SafeIcon icon={FiCheck} className="h-6 w-6 text-secondary-600 mt-1 flex-shrink-0" />
                <span className="text-gray-700">Towing and labor costs related to an incident</span>
              </div>
              <div className="flex items-start space-x-3">
                <SafeIcon icon={FiCheck} className="h-6 w-6 text-secondary-600 mt-1 flex-shrink-0" />
                <span className="text-gray-700">Coverage if you have an incident with someone who's underinsured</span>
              </div>
            </motion.div>
          </div>
          <motion.div
            initial={{opacity: 0, y: 30}}
            animate={{opacity: 1, y: 0}}
            transition={{duration: 0.6, delay: 0.6}}
            className="mt-12 p-6 bg-blue-50 rounded-lg"
          >
            <p className="text-lg text-gray-700 text-center">
              Even if you use just one car for both personal and business driving, you're exposed to different risks when you drive for business. You often need the more extensive coverage provided in a commercial auto policy.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 bg-gradient-to-r from-primary-600 to-primary-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h2
            initial={{opacity: 0, y: 30}}
            animate={{opacity: 1, y: 0}}
            transition={{duration: 0.6}}
            className="text-3xl md:text-4xl font-bold mb-6"
          >
            Protect Your Business Vehicles Today
          </motion.h2>
          <motion.p
            initial={{opacity: 0, y: 30}}
            animate={{opacity: 1, y: 0}}
            transition={{duration: 0.6, delay: 0.2}}
            className="text-xl mb-8"
          >
            Don't risk your business operations. Get comprehensive commercial auto coverage through our trusted partners.
          </motion.p>
          <motion.div
            initial={{opacity: 0, y: 30}}
            animate={{opacity: 1, y: 0}}
            transition={{duration: 0.6, delay: 0.4}}
          >
            <CTAButton 
              text="Start Your Free Quote Today" 
              size="xl" 
              className="bg-white text-primary-600 hover:bg-gray-100" 
            />
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default CommercialAutoPage;